package com.karawek.core.auth.api.local

import com.karawek.core.auth.api.Authenticator
import com.karawek.core.auth.model.AuthMethod
import com.karawek.core.auth.model.AuthSession
import com.karawek.core.auth.model.AuthenticatedUser
import com.karawek.core.auth.model.AuthenticationInfo
import com.karawek.core.auth.model.MockOtpHandle
import com.karawek.core.auth.model.NativeProvider
import com.karawek.core.auth.model.OAuthProvider
import com.karawek.core.auth.model.OtpChannel
import com.karawek.core.auth.model.OtpHandle
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import com.karawek.core.generated.resources.Res
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

@OptIn(ExperimentalTime::class)
class LocalAuthenticator : Authenticator {

    private val initiatedOtpHandles = mutableMapOf<String, OtpChannel>()
    private var identityRecords: List<IdentityRecord> = emptyList()
    private var isLoaded = false

    init {
        // Load identity records asynchronously
        CoroutineScope(Dispatchers.Default).launch {
            try {
                val json = Res.readBytes("files/local/identity.json").decodeToString()
                identityRecords = Json.decodeFromString<List<IdentityRecord>>(json)
                isLoaded = true
            } catch (e: Exception) {
                println("Failed to load identity.json: ${e.message}")
            }
        }
    }

    private suspend fun ensureLoaded() {
        // Wait until loaded
        while (!isLoaded) {
            kotlinx.coroutines.delay(10)
        }
    }

    override suspend fun getAvailableMethods(): Set<AuthMethod> {
        ensureLoaded()
        return setOf(
            OtpChannel.Email,
            OtpChannel.Phone,
            OtpChannel.WhatsApp,
            NativeProvider.Google,
            NativeProvider.Apple,
            OAuthProvider.Google,
            OAuthProvider.Apple,
            OAuthProvider.Facebook
        )
    }

    override suspend fun initiateOtp(
        channel: OtpChannel,
        value: String,
        loginOrCreate: Boolean,
    ): Result<OtpHandle> {
        ensureLoaded()
        // Accept any valid phone number or email address
        val handle = MockOtpHandle(
            token = generateToken(value),
            channel = channel,
            destination = value
        )
        initiatedOtpHandles[value] = channel
        return Result.success(handle)
    }

    override suspend fun verifyOtp(
        handle: OtpHandle,
        otpCode: String
    ): Result<AuthenticationInfo> {
        ensureLoaded()
        if (handle !is MockOtpHandle) {
            return Result.failure(Exception("Invalid OTP handle"))
        }

        // Check if this OTP was initiated
        if (!initiatedOtpHandles.containsKey(handle.destination)) {
            return Result.failure(Exception("OTP not initiated for ${handle.destination}"))
        }

        // Find the matching record, or create a new one
        val record = identityRecords.firstOrNull { record ->
            when (handle.channel) {
                OtpChannel.Email -> record.emailAddresses.contains(handle.destination)
                OtpChannel.Phone, OtpChannel.WhatsApp -> record.phoneNumbers.contains(handle.destination)
            }
        }

        // Any OTP code is valid
        initiatedOtpHandles.remove(handle.destination)

        return if (record != null) {
            Result.success(record.toAuthenticationInfo())
        } else {
            // Create authentication info for new identity
            val newIdentity = when (handle.channel) {
                OtpChannel.Email -> IdentityRecord(
                    userId = "identity-new-${handle.destination.hashCode()}",
                    emailAddresses = listOf(handle.destination)
                )
                OtpChannel.Phone, OtpChannel.WhatsApp -> IdentityRecord(
                    userId = "identity-new-${handle.destination.hashCode()}",
                    phoneNumbers = listOf(handle.destination)
                )
            }
            Result.success(newIdentity.toAuthenticationInfo())
        }
    }

    override suspend fun authenticateWithOAuth(
        provider: OAuthProvider
    ): Result<AuthenticationInfo> {
        ensureLoaded()
        val record = identityRecords.firstOrNull { it.oauthProviders.contains(provider.name) }
        return if (record != null) {
            Result.success(record.toAuthenticationInfo())
        } else {
            Result.failure(Exception("No user configured for OAuth provider: $provider"))
        }
    }

    override suspend fun authenticateWithNative(): Result<AuthenticationInfo> {
        ensureLoaded()
        // For native auth, we'll return the first available OAuth provider user
        val record = identityRecords.firstOrNull { it.oauthProviders.isNotEmpty() }
        return if (record != null) {
            Result.success(record.toAuthenticationInfo())
        } else {
            Result.failure(Exception("No user configured for native authentication"))
        }
    }

    override suspend fun signOut(): Result<Unit> {
        initiatedOtpHandles.clear()
        return Result.success(Unit)
    }

    override suspend fun registerPasskey(): Result<Unit> = Result.success(Unit)

    override suspend fun authenticateWithPasskey(): Result<AuthenticationInfo> =
        Result.failure(UnsupportedOperationException("Passkey is not supported in local auth"))

    override suspend fun renewSession(durationMinutes: Int): Result<Unit> = Result.success(Unit)

    private fun generateToken(value: String): String {
        return "mock_token_${value.hashCode()}_${Clock.System.now().toEpochMilliseconds()}"
    }
}

@Serializable
data class IdentityRecord(
    val userId: String,
    val firstName: String? = null,
    val middleName: String? = null,
    val lastName: String? = null,
    val emailAddresses: List<String> = emptyList(),
    val phoneNumbers: List<String> = emptyList(),
    val oauthProviders: List<String> = emptyList(),   // ["Google", "Apple", "Facebook"]
    val profilePictureUrl: String? = null,
) {
    @OptIn(ExperimentalTime::class)
    fun toAuthenticationInfo(): AuthenticationInfo {
        // Generate session fields dynamically
        val sessionToken = generateSessionToken()
        val sessionJwt = generateSessionJwt()
        val sessionId = generateSessionId()
        // Hard-coded expiration: January 1, 2026 00:00:00 UTC (1735689600000)
        val sessionExpirationEpochMillis = 1735689600000L

        return AuthenticationInfo(
            session = AuthSession(
                userId = userId,
                sessionToken = sessionToken,
                sessionJwt = sessionJwt,
                sessionId = sessionId,
                sessionExpiration = Instant.fromEpochMilliseconds(sessionExpirationEpochMillis)
            ),
            user = AuthenticatedUser(
                firstName = firstName,
                middleName = middleName,
                lastName = lastName,
                emailAddresses = emailAddresses,
                phoneNumbers = phoneNumbers,
                profilePictureUrl = profilePictureUrl
            )
        )
    }

    private fun generateSessionToken(): String {
        return "session_token_${userId}_${Clock.System.now().toEpochMilliseconds()}"
    }

    private fun generateSessionJwt(): String {
        // Generate a simple JWT-like token (not a real JWT, just for local development)
        return "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${userId}.local_dev_signature"
    }

    private fun generateSessionId(): String {
        return "session_${userId}_${Clock.System.now().toEpochMilliseconds()}"
    }
}
